import * as React from 'react';
import {
  Grid,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';

import CountryFlag from '../../../components/common/CountryFlag';
import { flagCodes } from '../../../util/options';
import { FragmentType, graphql, useFragment } from '../../../__generated__';
import { displayMoney } from 'gmd-utilities-package';

export const ClientRate_ItemFragment = graphql(/* GraphQL */ `
  fragment ClientRate_ItemFragment on Rate {
    id
    code
    source
    destination
    rate
  }
`);

export type ClientRateItemProps = {
  rate: FragmentType<typeof ClientRate_ItemFragment>;
  tenant: string;
  disableEdit?: boolean;
  onEdit: () => void;
  onFund: () => void;
  setSelectedRate: (rate: FragmentType<typeof ClientRate_ItemFragment>) => void;
  onDelete: () => void;
};

export type RateEditOptionProps = {
  disableEdit: boolean;
  open: boolean;
  onOpenFunding: () => void;
  handleClose: () => void;
  onOpenEdit: () => void;
  handleClick: (event: React.MouseEvent<HTMLElement>) => void;
  anchorEl: null | HTMLElement;
  onDelete: () => void;
};

const RateEditoptions = (props: RateEditOptionProps) => {
  const {
    disableEdit,
    open,
    anchorEl,
    onOpenFunding,
    handleClose,
    onOpenEdit,
    handleClick,
    onDelete,
  } = props;

  if (!disableEdit) {
    return (
      <React.Fragment>
        <IconButton
          edge="end"
          aria-label="more"
          id="long-button"
          aria-controls={open ? 'long-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          onClick={(e) => handleClick(e)}
          color={'primary'}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          id="long-menu"
          MenuListProps={{
            'aria-labelledby': 'long-button',
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: -80,
          }}
        >
          <MenuItem key={'edit'} onClick={onOpenEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            Edit
          </MenuItem>
          <MenuItem key={'addFunding'} onClick={onOpenFunding}>
            <ListItemIcon>
              <AddShoppingCartIcon fontSize="small" />
            </ListItemIcon>
            Add Funding
          </MenuItem>
          <MenuItem key={'delete'} onClick={onDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            Delete
          </MenuItem>
        </Menu>
      </React.Fragment>
    );
  } else {
    return <></>;
  }
};

const ClientRateItem = (props: ClientRateItemProps): JSX.Element => {
  const {
    onEdit,
    onFund,
    setSelectedRate,
    disableEdit = false,
    onDelete,
  } = props;
  const rate = useFragment(ClientRate_ItemFragment, props.rate);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const from = React.useMemo(() => {
    const amount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: rate.source,
    }).format(1);
    const flag = (
      <CountryFlag
        countryCode={flagCodes[rate.source]}
        otherProps={{
          style: {
            fontSize: '1.5em',
            lineHeight: '1.5em',
          },
        }}
      />
    );
    return { amount, flag };
  }, [rate.source]);

  const to = React.useMemo(() => {
    let amount = displayMoney(rate.rate, rate.destination, true);

    const flag = (
      <CountryFlag
        countryCode={flagCodes[rate.destination]}
        otherProps={{
          style: {
            fontSize: '1.5em',
            lineHeight: '1.5em',
          },
        }}
      />
    );
    return { amount, flag };
  }, [rate.destination, rate.rate]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setSelectedRate(props.rate);
  };

  const onOpenEdit = () => {
    handleClose();
    onEdit();
  };

  const onOpenFunding = () => {
    handleClose();
    onFund();
  };

  return (
    <Paper sx={{ mb: 2 }} elevation={3} key={rate.id}>
      <ListItem
        secondaryAction={
          <RateEditoptions
            disableEdit={disableEdit}
            open={open}
            handleClose={handleClose}
            handleClick={handleClick}
            onOpenFunding={onOpenFunding}
            onOpenEdit={onOpenEdit}
            anchorEl={anchorEl}
            onDelete={onDelete}
          />
        }
      >
        <ListItemText
          primary={
            <Grid container>
              <Grid item xs={4}>
                <Grid container>
                  <Grid item xs={4}>
                    {from.flag}
                  </Grid>
                  <Grid item xs={4}>
                    <DoubleArrowIcon color="primary" />
                  </Grid>
                  <Grid item xs={4}>
                    {to.flag}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          }
          secondary={
            <React.Fragment>
              <Typography
                fontWeight={'fontWeightMedium'}
                display={'inline-block'}
              >
                {from.amount} = {to.amount}
              </Typography>
            </React.Fragment>
          }
        />
      </ListItem>
    </Paper>
  );
};

export default ClientRateItem;
